import {
  ColumnDefinition,
  ColumnValue,
  IAccountSummary,
  IColumnSource,
  IExpandedGroupKeys,
  IExpandedRows,
  IRowSource
} from '@opengov/reporting-types';
import { GridApi, IRowNode, RowNode } from 'ag-grid-community';
import { MutableRefObject } from 'react';
import { ExtendedParams } from './serverSideDataSource.types';

export const AG_GRID_CACHE_BUSTER = -200000000;
export const AG_GRID_NEW_COLUMN_PLACEHOLDER_ID = 'newColumnPlaceholder';
export const AG_GRID_NEW_ROW_PLACEHOLDER_ID = 'newRowPlaceholder';
export const AG_GRID_MIN_COLUMN_WIDTH = 180;
export const AG_GRID_MIN_LEFTMOST_COLUMN_WIDTH = 317;
export const AG_GRID_MIN_LEFTMOST_COLUMN_WIDTH_WITH_MH_TOTALS_CHECKBOX = 410;
export const AG_GRID_DEFAULT_ROW_HEIGHT = 40;
export const AG_GRID_GROUP_COLUMN_ID = 'ag-Grid-AutoColumn';

export interface MultiHierarchyShowTotalsAtBottomContext {
  disabled: boolean;
  onChange(next: boolean): void;
}

/** One "Expand one level" an expand run is serving: the row level it opens, optionally under one row. */
export interface ExpandRunBounds {
  level: number;
  scopeRowId?: string;
}

/**
 * State of an in-progress "Expand All" or "Expand one level". Expanding a row triggers a
 * server-side child fetch, and rows below the viewport are only fetched once scrolled into view,
 * so the operation runs as a cascade across successive onModelUpdated cycles rather than in one
 * pass. `expandedRowIds` records the rows the run has already dealt with: rows the cascade opened,
 * and rows the user closed by hand while it ran. The cascade skips them, so a deliberate collapse
 * is not undone while the run keeps opening rows that load later.
 */
export interface ExpandAllRunState {
  active: boolean;
  expandedRowIds: Set<string>;
  /**
   * Bounds of each "Expand one level" the run is serving. Unset for "Expand All", which opens
   * every row that loads.
   */
  bounds?: ExpandRunBounds[];
}

export function createExpandAllRunState(): ExpandAllRunState {
  return { active: false, expandedRowIds: new Set<string>() };
}

export function createExpandOneLevelRunState(level: number, scopeRowId?: string): ExpandAllRunState {
  return { active: true, expandedRowIds: new Set<string>(), bounds: [{ level, scopeRowId }] };
}

/**
 * The run state after another "Expand one level" click: an active "Expand All" already opens
 * everything the click asks for, an active one-level run keeps its earlier bounds and gains the
 * new ones, and a finished run starts over.
 */
export function addExpandOneLevelRun(run: ExpandAllRunState, level: number, scopeRowId?: string): ExpandAllRunState {
  if (!run.active) return createExpandOneLevelRunState(level, scopeRowId);
  if (!run.bounds) return run;
  return { ...run, bounds: [...run.bounds, { level, scopeRowId }] };
}

export interface MDSContext extends ExtendedParams {
  currentRowLensesId: string;
  expandedGroupKeys: IExpandedGroupKeys;
  dataTableError: boolean;
  expandedRows: IExpandedRows;
  usesExpandedGroupKeys: boolean;
  inEditMode: boolean;
  selectedRowGroupId: string;
  rowGroups: IRowSource[];
  report: IAccountSummary;
  columnValues: ColumnValue[];
  /** Ids of the column groups `columnValues` was fetched for; null when unknown. */
  columnValueGroupIds?: string[] | null;
  dataUpdatedAt: string;
  updatedAt: string;
  rollupDimensionDataUpdatedAt?: string;
  canEditReport: boolean;
  reportConfigurationId: string;
  deletedRowGroupings: Record<number, number[]>;
  columnDefinitions: ColumnDefinition[];
  columnGroups: IColumnSource[];
  multiHierarchyShowTotalsAtBottom?: MultiHierarchyShowTotalsAtBottomContext;
  expandAllRunRef: MutableRefObject<ExpandAllRunState>;
  lockRowExpansion?: boolean;
}

export const getCurrentAgGridContext = <T extends MDSContext = MDSContext>(common: {
  context: { get: () => T };
}): T => {
  return common.context.get();
};

export const getNodeCount = (api: GridApi): number => {
  let count = 0;
  api.forEachNode(() => count++);
  return count;
};

export const selectNodeAndChildren = (api: GridApi, targetNode?: RowNode | IRowNode) => {
  api.clearRangeSelection();

  if (targetNode) {
    let childrenCount = 0;
    if (targetNode.expanded) {
      api.forEachNode((row: IRowNode) => {
        let parent: IRowNode | null = row.parent ?? null;
        while (parent) {
          if (parent.data?.id === targetNode.data.id) {
            childrenCount++;
            parent = null;
          } else {
            parent = parent.expanded ? (parent.parent ?? null) : null;
          }
        }
      });
    }
    if (targetNode.rowIndex === null) return;
    api.addCellRange({
      rowStartIndex: targetNode.rowIndex,
      rowEndIndex: targetNode.rowIndex + childrenCount,
      columns: [api.getColumn(AG_GRID_GROUP_COLUMN_ID) ?? AG_GRID_GROUP_COLUMN_ID, ...(api.getColumns() ?? [])]
    });
  }
};
